import React from 'react';
import useTranslation from '../../../Hooks/useTranslation';
import RemoveButton from '../../../Components/Button/RemoveButton';
import { EPrescriptionHVLPMedicament } from './EPrescriptionMedicamentsHVLPForm';
import EPrescriptionMedicamentWarnings from './EPrescriptionMedicamentWarnings';
// @ts-ignore
import styles from './EPrescriptionMedicamentsListItem.module.scss';
import LinkButton from '../../../Components/Button/LinkButton';
import { Theme } from '../../../Enums/Theme.enum';

interface EPrescriptionMedicamentsListItemHVLProps {
	item: EPrescriptionHVLPMedicament;
	onRemove: (item: EPrescriptionHVLPMedicament) => void;
	onEdit: (item: EPrescriptionHVLPMedicament) => void;
}

const EPrescriptionMedicamentsListItemHVLP: React.FC<
	EPrescriptionMedicamentsListItemHVLProps
> = ({ item, onRemove, onEdit }) => {
	const { t } = useTranslation();

	return (
		<div className={styles.wrapper}>
			<div className={styles.content}>
				<div className={styles.row}>
					<div className={styles.title}>
						<div className={styles.value}>
							{item?.item?.name ?? t('base.shared.notSpecified')}
						</div>
						<div className={styles.controls}>
							<RemoveButton
								onClick={() => onRemove(item)}
								title='base.shared.remove'
							/>
							<LinkButton
								theme={Theme.BLUE}
								onClick={() => onEdit(item)}
								title='base.shared.edit'
							/>
						</div>
					</div>
				</div>
				<div className={styles.row}>
					<div className={styles.item}>
						<div className={styles.name}>
							{t('component.ePrescription.code')}
						</div>
						<div className={styles.value}>
							{item.item.code ?? t('base.shared.notSpecified')}
						</div>
					</div>
					<div className={styles.item}>
						<div className={styles.name}>
							{t('component.ePrescription.form')}
						</div>
						<div className={styles.value}>
							{item.item.form ?? t('base.shared.notSpecified')}
						</div>
					</div>
					<div className={styles.item}>
						<div className={styles.name}>
							{t('component.ePrescription.strength')}
						</div>
						<div className={styles.value}>
							{item.item.strength ?? t('base.shared.notSpecified')}
						</div>
					</div>
					<div className={styles.item}>
						<div className={styles.name}>
							{t('component.ePrescription.packaging')}
						</div>
						<div className={styles.value}>
							{item.item.packaging ?? t('base.shared.notSpecified')}
						</div>
					</div>
					<div className={styles.item}>
						<div className={styles.name}>
							{t('component.ePrescription.amount')}
						</div>
						<div className={styles.value}>{item.quantity}</div>
					</div>
					<div className={styles.item}>
						<div className={styles.name}>
							{t('component.ePrescription.dosage')}
						</div>
						<div className={styles.value}>{item.manual}</div>
					</div>
					<div className={styles.item}>
						<div className={styles.name}>
							{t('component.ePrescription.notToBeMistaken')}
						</div>
						<div className={styles.value}>
							{item.doNotConfuse ? t('base.yes') : t('base.no')}
						</div>
					</div>
					<div className={styles.item}>
						<div className={styles.name}>
							{t('component.ePrescription.overStep')}
						</div>
						<div className={styles.value}>
							{item.overrun ? t('base.yes') : t('base.no')}
						</div>
					</div>
					<div className={styles.item}>
						<div className={styles.name}>
							{t('component.ePrescription.applicationNumber')}
						</div>
						<div className={styles.value}>
							{item.requestNumber ?? t('base.shared.notSpecified')}
						</div>
					</div>
					<div className={styles.item}>
						<div className={styles.name}>
							{t('component.ePrescription.payment')}
						</div>
						<div className={styles.value}>
							{item.payment?.label ?? t('base.shared.notSpecified')}
						</div>
					</div>
					{item.item.priceRawFormatted && (
						<div className={styles.item}>
							<div className={styles.name}>
								{t('component.ePrescription.priceFinal')}
							</div>
							<div className={styles.value}>{item.item.priceRawFormatted}</div>
						</div>
					)}
					{item.item.priceInsuranceCompanyRawFormatted && (
						<div className={styles.item}>
							<div className={styles.name}>
								{t('component.ePrescription.coveredByInsurance')}
							</div>
							<div className={styles.value}>
								{item.item.priceInsuranceCompanyRawFormatted}
							</div>
						</div>
					)}
					{item.item.priceExtraFormatted && (
						<div className={styles.item}>
							<div className={styles.name}>
								{t('component.ePrescription.paidByPatient')}
							</div>
							<div className={styles.value}>
								{item.item.priceExtraFormatted}
							</div>
						</div>
					)}
				</div>
				<hr style={{ width: '100%' }} />
				<div className={styles.row}>
					<div className={styles.item}>
						<div className={styles.name}>{t('base.shared.diagnosis')}</div>
						<div className={styles.value}>
							{item.diagnosis?.label ?? t('base.shared.notSpecified')}
						</div>
					</div>
					<div className={styles.item}>
						<div className={styles.name}>
							{t('component.ePrescription.additionalDiagnosis')}
						</div>
						<div className={styles.value}>
							{item.associatedDiagnosis?.label ?? t('base.shared.notSpecified')}
						</div>
					</div>
				</div>
				<div className={styles.warnings}>
					<EPrescriptionMedicamentWarnings item={item.item} />
				</div>
			</div>
		</div>
	);
};

export default EPrescriptionMedicamentsListItemHVLP;
